/**
 * --------------------------------------------------------------------------
 * Liander Bootstrap process.js
 * --------------------------------------------------------------------------
 */

import {
  defineJQueryPlugin,
  isVisible
} from '../../node_modules/bootstrap/js/src/util/index.js'
import EventHandler from '../../node_modules/bootstrap/js/src/dom/event-handler.js'
import SelectorEngine from '../../node_modules/bootstrap/js/src/dom/selector-engine.js'
import BaseComponent from '../../node_modules/bootstrap/js/src/base-component.js'

/**
 * Constants
 */

const NAME = 'processoverview'
const DATA_KEY = 'lbs.processoverview'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'

const TOP_CORRECTION = -100

const EVENT_LOAD_DATA_API = `load${EVENT_KEY}${DATA_API_KEY}`

const CLASS_NAME_STICKY = 'sticky'

const SELECTOR_MENU = '.main-nav'
const SELECTOR_MOBILE_MENU = '.main-nav-mobile'
const SELECTOR_PROCESS_OVERVIEW = '.process-overview'
const SELECTOR_PROCESS_CIRCLE_LAST = '.process-line-index.last .process-circle'
const SELECTOR_PROCESS_CIRCLE_START = '.process-circle-start'
const SELECTOR_PROCESS_ITEM_GROUP = '.process-item-group'
const SELECTOR_PROCESS_ITEM = '.process-item'
const SELECTOR_PROCESS_START = '.process-start'

const Default = {}

const DefaultType = {}

/**
 * Class definition
 */

class ProcessOverview extends BaseComponent {
  constructor(element, config) {
    super(element, config)

    this._desktopView = window.matchMedia('(min-width: 992px)')

    this._initProcessOverview()
  }

  // Getters
  static get Default() {
    return Default
  }

  static get DefaultType() {
    return DefaultType
  }

  static get NAME() {
    return NAME
  }

  // Public

  // Private
  _initProcessOverview() {
    this._checkCircleStart()
    this._addEventListeners()

    if (this._desktopView.matches) {
      this._compressProcessOverview()
    }
  }

  _addEventListeners() {
    window.addEventListener('scroll', () => {
      this._checkCircleStart()
    })

    this._desktopView.addEventListener('change', desktopView => this._handleDesktopViewChanged(desktopView))
    // this._desktopView.addListener((desktopView) => this._handleDesktopViewChanged(desktopView))
  }

  _getCircleStart() {
    return SelectorEngine.findOne(SELECTOR_PROCESS_CIRCLE_START)
  }

  _getItemGroups() {
    return SelectorEngine.find(SELECTOR_PROCESS_ITEM_GROUP)
  }

  _getLastCircle() {
    return SelectorEngine.findOne(SELECTOR_PROCESS_CIRCLE_LAST)
  }

  _getMenu() {
    return SelectorEngine.findOne(SELECTOR_MENU)
  }

  _getMobileMenu() {
    return SelectorEngine.findOne(SELECTOR_MOBILE_MENU)
  }

  _getStart() {
    return SelectorEngine.findOne(SELECTOR_PROCESS_START)
  }

  _checkCircleStart() {
    const lastCircleOffset = {
      top: this._getLastCircle().getBoundingClientRect().top + window.scrollY || 0
    }
    const padding = 8
    const circleStartHeight = this._getCircleStart().offsetHeight
    const startOffsetTop = this._getStart().getBoundingClientRect().top + window.scrollY
    const scrollTopTreshold = startOffsetTop - (circleStartHeight / 2)

    let menuCorrection = 0
    if (isVisible(this._getMobileMenu())) {
      menuCorrection = this._getMobileMenu().offsetHeight
    } else if (isVisible(this._getMenu())) {
      menuCorrection = this._getMenu().offsetHeight
    }

    if (menuCorrection) {
      menuCorrection += padding
    }

    if (window.scrollY > scrollTopTreshold - menuCorrection) {
      if (lastCircleOffset.top === 0 || window.scrollY < lastCircleOffset.top - menuCorrection) {
        this._getCircleStart().classList.add(CLASS_NAME_STICKY)
        if (menuCorrection) {
          this._getCircleStart().style.top = `${menuCorrection}px`
        }
      } else {
        this._getCircleStart().classList.remove(CLASS_NAME_STICKY)
        this._getCircleStart().style.top = ''
      }
    } else {
      this._getCircleStart().classList.remove(CLASS_NAME_STICKY)
      this._getCircleStart().style.top = ''
    }
  }

  _handleDesktopViewChanged(desktopView) {
    if (desktopView.matches) {
      this._compressProcessOverview()
    } else {
      this._uncompressProcessOverview()
    }
  }

  _compressProcessOverview() {
    const itemGroups = this._getItemGroups()
    for (const itemGroup of itemGroups) {
      const items = SelectorEngine.find(SELECTOR_PROCESS_ITEM, itemGroup)

      for (const [index, item] of items.entries()) {
        if (index > 0) {
          item.style.top = TOP_CORRECTION * index
        }
      }

      itemGroup.style.height = itemGroup.offsetHeight + (TOP_CORRECTION * (items.length - 1))
    }
  }

  _uncompressProcessOverview() {
    const itemGroups = this._getItemGroups()
    for (const itemGroup of itemGroups) {
      const items = SelectorEngine.find(SELECTOR_PROCESS_ITEM, itemGroup)

      for (const [index, item] of items.entries()) {
        if (index > 0) {
          item.style.top = ''
        }
      }

      itemGroup.style.height = ''
    }
  }

  // Static
  static jQueryInterface(config) {
    return this.each(function () {
      const data = ProcessOverview.getOrCreateInstance(this, config)

      if (typeof config === 'string') {
        if (data[config] === undefined || config.startsWith('_') || config === 'constructor') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config]()
      }
    })
  }
}

/**
 * Data API implementation
 */

EventHandler.on(window, EVENT_LOAD_DATA_API, () => {
  const processOverviews = SelectorEngine.find(SELECTOR_PROCESS_OVERVIEW)

  for (const processOverview of processOverviews) {
    ProcessOverview.getOrCreateInstance(processOverview)
  }
})

/**
 * jQuery
 */

defineJQueryPlugin(ProcessOverview)

export default ProcessOverview
